import React, { useState, useEffect, useMemo } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer as MuiDrawer,
  CssBaseline,
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  List,
  useMediaQuery,
} from "@mui/material";
import HomeRounded from "@mui/icons-material/HomeRounded";
import MenuIcon from "@mui/icons-material/Menu";
import { ColorModeThemeProvider } from "context/ColorMode";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "components/styled/Logo";
import StatusBanner from "components/dashboard/StatusBanner";
import { useAuth } from "hooks/useAuth";
import { useClinicPending } from "hooks/useClinicPending";
import { useClinicRejected } from "hooks/useClinicRejected";
import { DrawerContext } from "context/DrawerContext";
import { TopBarContext } from "context/TopBarContext";
import { APP_BAR_HEIGHT } from "constants/layout";
import AccountMenu from "./AccountMenu";
import AppSwitcher from "./AppSwitcher";
import SidebarIcon from "./SidebarIcon";
import NavListItem from "components/common/NavListItem";
import {
  LearnerDrawer,
  PatientDrawer,
  ProfessionalDrawer,
  FacilityDrawer,
  AdminDrawer,
  ChartMindManagerDrawer,
  GlobalAdminDrawer,
  RegionalAdminDrawer,
} from "./DrawerComponents";

const drawerWidth = 280;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  // The page's own colour, so the bar and the page read as one surface.
  backgroundColor: theme.palette.background.default,
  // Dark-mode Paper adds an elevation tint via backgroundImage; keep the bar flat.
  backgroundImage: "none",
  boxShadow: "none",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(!open && {
    width: `calc(100% - ${theme.spacing(7)} - 1px)`,
    [theme.breakpoints.up("sm")]: {
      width: `calc(100% - ${theme.spacing(8)} - 1px)`,
    },
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    ...(open ? openedMixin(theme) : closedMixin(theme)),
  },
  ...(open ? openedMixin(theme) : closedMixin(theme)),
}));

const DashboardNavContent = ({ children }) => {
  const theme = useTheme();
  const { t } = useTranslation("home");
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const {
    user,
    subscription,
    userData,
    activeSubscriptionRole,
    logout,
    isGlobalAdmin,
    isRegionalAdmin,
  } = useAuth();
  const navigate = useNavigate();
  // Reserve scroll space for the fixed bottom clinic status banner (pending or
  // rejected) so it never covers the last line of page content (StatusBanner
  // renders the bar).
  const clinicPending = useClinicPending();
  const clinicRejected = useClinicRejected();
  const clinicStatusBanner = clinicPending || clinicRejected;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  // The top bar's two page-filled slots (TopBarContext). Callback refs, so pages
  // re-render with the nodes once they exist. `register` counts the pages filling
  // them, so the phone's logo can give up its room to the page's own things.
  const [titleNode, setTitleNode] = useState(null);
  const [actionsNode, setActionsNode] = useState(null);
  const [slotCount, setSlotCount] = useState(0);
  const topBar = useMemo(
    () => ({
      titleNode,
      actionsNode,
      register: () => {
        setSlotCount((n) => n + 1);
        return () => setSlotCount((n) => n - 1);
      },
    }),
    [titleNode, actionsNode],
  );
  const pageFillsBar = slotCount > 0;

  // At rest the bar is see-through with no rule, so bar and page read as one surface;
  // once the page scrolls it turns solid with a hairline, so content never slides
  // under an invisible edge.
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const mainContent = document.querySelector("[data-main-content]");
    if (!mainContent) return undefined;
    const handleScroll = (e) => setIsScrolled(e.target.scrollTop > 0);
    mainContent.addEventListener("scroll", handleScroll);
    return () => mainContent.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleDesktopDrawerOpen = () => setDrawerOpen(true);
  const handleDesktopDrawerClose = () => setDrawerOpen(false);

  const drawerExpanded = isDesktop ? drawerOpen : mobileOpen;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // User is verified if email verified OR phone verified
  const isVerified = user?.emailVerified || userData?.phoneVerified;
  // Same rule as the sidebar's sections and Settings: admins always qualify.
  const canUseApp = isVerified || isGlobalAdmin || isRegionalAdmin;

  const drawer = (
    <DrawerContext.Provider value={drawerExpanded}>
      <DrawerHeader
        sx={{
          justifyContent: drawerOpen ? "space-between" : "center",
          // No side padding on the folded rail: 16px a side leaves 32px for a 40px
          // button, which then overflows and sits off the nav icons' centre line.
          px: drawerOpen ? 2 : 0,
        }}
      >
        {drawerOpen && (
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Logo />
          </Box>
        )}
        <IconButton
          aria-label={drawerOpen ? t("nav.collapseDrawer") : t("nav.expandDrawer")}
          onClick={
            isDesktop
              ? drawerOpen
                ? handleDesktopDrawerClose
                : handleDesktopDrawerOpen
              : handleDrawerToggle
          }
          sx={{ display: { xs: "none", sm: "inline-flex" } }}
        >
          <SidebarIcon />
        </IconButton>
        <Box sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center" }}>
          <IconButton
            aria-label={t("nav.closeDrawer")}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <SidebarIcon />
          </IconButton>
          <Logo />
        </Box>
      </DrawerHeader>
      {/* Scrolls on its own so the account row stays pinned to the bottom. */}
      <Box
        onClick={isDesktop ? undefined : handleDrawerToggle}
        sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}
      >
        {/* Home sits above every role's sections, once, rather than in each drawer:
            some roles stack more than one drawer. */}
        {canUseApp && (
          <List>
            <NavListItem icon={<HomeRounded />} text={t("nav.home")} link="/dashboard" />
          </List>
        )}
        {isGlobalAdmin ? (
          <GlobalAdminDrawer />
        ) : isRegionalAdmin ? (
          <RegionalAdminDrawer />
        ) : (
          <>
            {userData?.role === "facility" && isVerified && (
              <FacilityDrawer
                activeSubscriptionRole={activeSubscriptionRole}
                userData={userData}
              />
            )}

            {userData?.role === "patient" && isVerified && (
              <PatientDrawer subscription={subscription} userData={userData} />
            )}

            {userData?.role === "professional" && isVerified && (
              <ProfessionalDrawer
                subscription={subscription}
                userData={userData}
              />
            )}

            {userData?.role === "admin" && isVerified && (
              <ChartMindManagerDrawer />
            )}

            {userData?.role === "learner" && isVerified && <LearnerDrawer />}

            {isVerified && <AdminDrawer isGlobalAdmin={isGlobalAdmin} />}
          </>
        )}
      </Box>
    </DrawerContext.Provider>
  );

  const closedDrawerWidth = `calc(${theme.spacing(8)} + 1px)`;
  const mainWidth = isDesktop
    ? drawerOpen
      ? `calc(100% - ${drawerWidth}px)`
      : `calc(100% - ${closedDrawerWidth})`
    : "100%";

  return (
    <TopBarContext.Provider value={topBar}>
      <Box sx={{ display: "flex" }}>
        {/* enableColorScheme also darkens native scrollbars and form controls. */}
        <CssBaseline enableColorScheme />
        {/* The one top bar, at every screen size. Its left side and the icons before
            the divider belong to the page (TopBarTitle / TopBarActions); the app
            switcher and avatar after it are the same on every page. */}
        <AppBar
          position="fixed"
          open={isDesktop && drawerOpen}
          sx={{
            borderBottom: 1,
            borderColor: isScrolled ? "divider" : "transparent",
            // See-through at rest, so a page's background (the Home glow) runs up
            // behind the bar; solid once content can slide under it.
            ...(!isScrolled && { backgroundColor: "transparent" }),
            ...(!isDesktop && {
              width: "100%",
              marginLeft: 0,
              zIndex: theme.zIndex.appBar,
            }),
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: APP_BAR_HEIGHT },
              px: { xs: 1, sm: 2 },
              gap: 0.5,
            }}
          >
            <IconButton
              aria-label={t("nav.openDrawer")}
              onClick={handleDrawerToggle}
              sx={{ display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            {!pageFillsBar && (
              <Box sx={{ display: { sm: "none" }, ml: 1 }}>
                <Logo />
              </Box>
            )}
            <Box
              ref={setTitleNode}
              sx={{
                flex: 1,
                minWidth: 0,
                alignSelf: "stretch",
                display: "flex",
                alignItems: "center",
              }}
            />
            <Box
              ref={setActionsNode}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                // A rule between the page's icons and the shared ones, when there are any.
                "&:not(:empty)": {
                  borderRight: 1,
                  borderColor: "divider",
                  pr: 1,
                  mr: 0.5,
                },
              }}
            />
            {canUseApp && <AppSwitcher opensDown />}
            <AccountMenu
              topBar
              // Settings needs a verified account, as before; admins always have one.
              showSettings={canUseApp}
              onLogout={handleLogout}
            />
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ flexShrink: { sm: 0 } }}
          aria-label={t("nav.navigation")}
        >
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            onTransitionEnd={handleDrawerTransitionEnd}
            onClose={handleDrawerClose}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                bgcolor: "background.sidebar",
              },
            }}
          >
            {drawer}
          </MuiDrawer>
          <Drawer
            variant="permanent"
            open={drawerOpen}
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": { bgcolor: "background.sidebar" },
            }}
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          data-main-content
          sx={{
            flexGrow: 1,
            margin: "auto",
            p: 3,
            paddingTop: `${APP_BAR_HEIGHT}px`,
            paddingBottom: clinicStatusBanner ? { xs: "104px", sm: "56px" } : 0,
            paddingLeft: 0,
            paddingRight: 0,
            width: mainWidth,
            height: "100dvh",
            overflow: "auto",
            position: "relative",
          }}
        >
          <StatusBanner />
          {children}
        </Box>
      </Box>
    </TopBarContext.Provider>
  );
};

// Dark mode state lives at the app root (App.js) so it carries across the auth pages
// and the dashboard; this applies it to the shell.
const DashboardNav = ({ children }) => (
  <ColorModeThemeProvider>
    <DashboardNavContent>{children}</DashboardNavContent>
  </ColorModeThemeProvider>
);

export default DashboardNav;
